import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Settings, Save, Globe, DollarSign, Share2, Send, MessageSquare, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SettingsTab = ({ 
  settings: initialSettings, 
  token, 
  onRefresh 
}) => {
  const [settings, setSettings] = useState(initialSettings);
  const [saving, setSaving] = useState(false);
  const [testingDiscord, setTestingDiscord] = useState(false);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved!');
      onRefresh();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestDiscord = async () => {
    setTestingDiscord(true);
    try {
      const res = await axios.post(`${API}/admin/notifications/test-discord`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message || 'Test Discord notification sent successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Discord notification. Check DISCORD_WEBHOOK_URL in server environment.');
    } finally {
      setTestingDiscord(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <DollarSign className="w-5 h-5 text-green-500" />
            Default Prize Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300">Default Prize Amount</Label>
              <Input
                type="number"
                value={settings.prize_amount || ''}
                onChange={(e) => setSettings({ ...settings, prize_amount: parseFloat(e.target.value) || 0 })}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-gray-300">Currency</Label>
              <Input
                value={settings.prize_currency || 'USD'}
                onChange={(e) => setSettings({ ...settings, prize_currency: e.target.value })}
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Globe className="w-5 h-5 text-blue-500" />
            Global Challenge Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-gray-300">Default Challenge Title</Label>
            <Input
              value={settings.challenge_title || ''}
              onChange={(e) => setSettings({ ...settings, challenge_title: e.target.value })}
              placeholder="Submit Your Best Video"
              className="bg-zinc-900 border-zinc-800"
            />
          </div>
          
          <div>
            <Label className="text-gray-300">Default Rules</Label>
            <textarea
              value={settings.challenge_rules || ''}
              onChange={(e) => setSettings({ ...settings, challenge_rules: e.target.value })}
              placeholder="Enter default rules for new challenges..."
              rows={4}
              className="w-full px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-md text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="contest_active"
              checked={settings.contest_active}
              onChange={(e) => setSettings({ ...settings, contest_active: e.target.checked })}
              className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-red-600 focus:ring-red-500"
            />
            <Label htmlFor="contest_active" className="text-gray-300">
              Contest Active (Allow new submissions globally)
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* Discord Webhook Testing */}
      <Card className="glass-card border-indigo-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-400">
            <MessageSquare className="w-5 h-5" />
            Discord Webhook Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-400">
            Push notifications for live and scheduled challenges automatically send to your Discord channel via <code className="text-indigo-300 bg-zinc-900 px-1 py-0.5 rounded text-xs">DISCORD_WEBHOOK_URL</code>.
          </p>
          <div className="flex items-center justify-between p-3 bg-zinc-900/60 border border-zinc-800 rounded-lg">
            <span className="text-sm text-gray-300">Test Discord Channel Webhook</span>
            <Button
              type="button"
              onClick={handleTestDiscord}
              disabled={testingDiscord}
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {testingDiscord ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Test Webhook
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Social Links for Promo Pages */}
      <Card className="glass-card border-pink-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-pink-400">
            <Share2 className="w-5 h-5" />
            Social Links (for OBS Overlays)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-400">
            These will be displayed on OBS overlays and promotional materials
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <svg className="w-4 h-4 text-purple-500" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                </svg>
                Twitch Username
              </Label>
              <Input
                value={settings.site_twitch || ''}
                onChange={(e) => setSettings({ ...settings, site_twitch: e.target.value.replace('@', '') })}
                placeholder="cesarm"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-gray-300 flex items-center gap-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
                Twitter/X Username
              </Label>
              <Input
                value={settings.site_twitter || ''}
                onChange={(e) => setSettings({ ...settings, site_twitter: e.target.value.replace('@', '') })}
                placeholder="cesarm"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button 
        onClick={handleSaveSettings} 
        disabled={saving}
        className="w-full bg-red-600 hover:bg-red-700"
      >
        <Save className="w-4 h-4 mr-2" />
        {saving ? 'Saving...' : 'Save Settings'}
      </Button>
    </div>
  );
};

export default SettingsTab;

